import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useLenis from "@/hooks/useLenis";
import MetaPixel from "@/analytics/MetaPixel";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Impact from "@/pages/Impact";
import ImpactDetail from "@/pages/ImpactDetail";
import Knowledge from "@/pages/Knowledge";
import KnowledgeDetail from "@/pages/KnowledgeDetail";
import DiscoverContext from "@/pages/DiscoverContext";
import Contact from "@/pages/Contact";
import ThankYou from "@/pages/ThankYou";
import { Privacy, Terms } from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import Team from "@/pages/Team";
import News from "@/pages/News";
import NewsDetail from "@/pages/NewsDetail";
import Publications from "@/pages/Publications";
import Profile from "@/pages/Profile";
import Careers from "@/pages/Careers";
import { AdminAuthProvider } from "@/admin/AdminAuthContext";
import AdminLogin from "@/admin/AdminLogin";
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/AdminDashboard";
import AdminList from "@/admin/AdminList";
import AdminForm from "@/admin/AdminForm";
import AdminDownloads from "@/admin/AdminDownloads";
import { REDIRECTS } from "@/content/site";

function ChromeGate({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return isAdmin ? children : (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "auto" }));
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);
  return null;
}

function App() {
  useLenis();
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <MetaPixel />
        <AdminAuthProvider>
          <ChromeGate>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/impact" element={<Impact />} />
            <Route path="/impact/:slug" element={<ImpactDetail />} />
            <Route path="/knowledge" element={<Knowledge />} />
            <Route path="/publications" element={<Publications />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsDetail />} />
            <Route path="/team" element={<Team />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/knowledge/:slug" element={<KnowledgeDetail />} />
            <Route path="/discover-context" element={<DiscoverContext />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* Admin (hidden from nav, protected inside AdminLayout) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="publications" element={<AdminList entity="publications" />} />
              <Route path="publications/new" element={<AdminForm entity="publications" />} />
              <Route path="publications/edit/:id" element={<AdminForm entity="publications" />} />
              <Route path="news" element={<AdminList entity="news" />} />
              <Route path="news/new" element={<AdminForm entity="news" />} />
              <Route path="news/edit/:id" element={<AdminForm entity="news" />} />
              <Route path="projects" element={<AdminList entity="projects" />} />
              <Route path="projects/new" element={<AdminForm entity="projects" />} />
              <Route path="projects/edit/:id" element={<AdminForm entity="projects" />} />
              <Route path="downloads" element={<AdminDownloads />} />
            </Route>

            {/* Legacy 301 redirect map (client-side; true 301 configured at edge in production) */}
            {Object.entries(REDIRECTS).map(([from, to]) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}

            <Route path="*" element={<NotFound />} />
          </Routes>
          </ChromeGate>
        </AdminAuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
